import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import type { SavedViewRecord } from '@/utils/savedViews';

/** The dialogs a saved view's "…" menu in the sidebar can open. */
export type SavedViewRowDialog = 'rename' | 'appearance' | 'delete';

/**
 * Which saved view the sidebar is renaming, recolouring or deleting.
 *
 * The section sidebar holds one set of these dialogs, in SavedViewRowDialogs,
 * rather than one set per row. Each row's "…" menu calls `openDialog` with its
 * own view. The rows then carry no dialogs, so changing area removes rows
 * without removing any dialog's Teleport (see useMountedWhileOpen).
 */
export interface SavedViewRowActions {
    /** The view the open dialog is about. It stays set while the dialog fades out. */
    targetView: Ref<SavedViewRecord | null>;
    /** The dialog that is open, or null when none is. */
    openDialogName: Ref<SavedViewRowDialog | null>;
    openDialog: (dialog: SavedViewRowDialog, view: SavedViewRecord) => void;
    closeDialog: () => void;
}

const SAVED_VIEW_ROW_ACTIONS: InjectionKey<SavedViewRowActions> = Symbol('saved-view-row-actions');

/** Called once by the section sidebar, which also renders SavedViewRowDialogs. */
export function provideSavedViewRowActions(): SavedViewRowActions {
    const targetView = ref<SavedViewRecord | null>(null);
    const openDialogName = ref<SavedViewRowDialog | null>(null);
    const actions: SavedViewRowActions = {
        targetView,
        openDialogName,
        openDialog(dialog, view) {
            targetView.value = view;
            openDialogName.value = dialog;
        },
        closeDialog() {
            openDialogName.value = null;
        },
    };
    provide(SAVED_VIEW_ROW_ACTIONS, actions);
    return actions;
}

/** Used by a row's "…" menu and by the dialogs themselves. */
export function useSavedViewRowActions(): SavedViewRowActions {
    const actions = inject(SAVED_VIEW_ROW_ACTIONS, null);
    if (actions === null) {
        throw new Error('A saved view row menu must sit inside the section sidebar, which provides its dialogs.');
    }
    return actions;
}
